import React, { useState, useEffect, useMemo } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Table,
  Form, 
  Modal, 
  Spinner, 
  Alert,
  Badge 
} from 'react-bootstrap';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getPaginationRowModel,
  getSortedRowModel,
  createColumnHelper,
} from '@tanstack/react-table';
import { useAuth } from "react-oidc-context";
import { useUser } from "../context/UserContext";
import { orgPropAPI } from "../utils/apiClient";
import LoadingSpinner from "../components/LoadingSpinner";


const columnHelper = createColumnHelper();

// Define demo account hash - REPLACE WITH YOUR ACTUAL DEMO ACCOUNT HASH
const DEMO_ACCOUNT_HASH = '60ea09b922c70e13788e44b50ca0fa89b05edad1de460477504077f3f7cc408c';

const fakeDemoUsers = [
  { email: 'definitely.real.person@sunnysphere.com', role: 'admin' },
  // { email: 'notarobot@sunnysphere.com', role: 'user' },
  // { email: 'john.smith123456@sunnysphere.com', role: 'admin' },
  { email: 'actual.employee@sunnysphere.com', role: 'user' },
  { email: 'human.mchuman@sunnysphere.com', role: 'user' },
  // { email: 'totallylegit@sunnysphere.com', role: 'user' },
  { email: 'real.worker.bee@sunnysphere.com', role: 'user' },
  { email: 'steve.placeholder@sunnysphere.com', role: 'user' },
  // { email: 'employee.number.42@sunnysphere.com', role: 'user' },
  // { email: 'definitely.not.ai@sunnysphere.com', role: 'user' }
];

function ProfilePage() {
  const auth = useAuth();
  useEffect(() => {
    document.title = 'Profile Page - LostLogic';
  }, []);
  const { orgHash, role, isSuperadmin } = useUser();
  
  // State for profile data
  const [profileData, setProfileData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [usersData, setUsersData] = useState([]);
  
  // Loading states
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // Track if initial load is complete
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  
  // Settings toggles
  const [hideSuccessToasts, setHideSuccessToasts] = useState(false);
  const [rogueMonitoring, setRogueMonitoring] = useState(true);
  const [updatingRogueMonitoring, setUpdatingRogueMonitoring] = useState(false);
  const [newOwnerAlert, setNewOwnerAlert] = useState(false);
  const [newHostAlert, setNewHostAlert] = useState(false);
  
  // Add user modal
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  const [addingUser, setAddingUser] = useState(false);
  
  // Table pagination
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  
  // Errors
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Combined loading state
  const isAnyLoading = loadingProfile || loadingStats || loadingUsers;
  
  // Check if this is demo account and user can edit
  const isDemoAccount = orgHash === DEMO_ACCOUNT_HASH;
  const canEditSettings = isSuperadmin || !isDemoAccount;
  
  // Check if user limit reached
  const isUserLimitReached = useMemo(() => {
    if (!profileData || profileData.users_limit === 'unlimited') {
      return false;
    }
    
    const limit = parseInt(profileData.users_limit);
    return usersData.length >= limit;
  }, [profileData, usersData]);
  
  // Fetch user's current rogue monitoring preference
  const fetchUserPreferences = async () => {
    const token = auth.user?.access_token;
    const userId = auth.user?.profile?.sub;
    
    if (!token || !userId) return;
    
    try {
      // Get user data from org users list to check current preference
      const data = await orgPropAPI.getOrgUsers(token, orgHash);
      const currentUser = data.users?.find(u => u.userID === userId);
      
      if (currentUser) {
        // Set rogue monitoring state based on user's preference
        // Default to true if not set
        setRogueMonitoring(currentUser.rogue_monitoring_allowed !== false);
      }
    } catch (err) {
      console.error('Error fetching user preferences:', err);
      // Default to enabled if error
      setRogueMonitoring(true);
    }
  };

  //handle onboarding
  const handleRestartTour = (e) => {
    if (e.target.checked) {
      localStorage.removeItem('onboardingCompleted');
      window.location.href = '/user-management';
    }
  };
  
  // Handle rogue monitoring toggle
  const handleToggleRogueMonitoring = async (e) => {
    const newValue = e.target.checked;
    const token = auth.user?.access_token;
    const userId = auth.user?.profile?.sub;
    
    if (!token || !userId) {
      setError('Authentication required');
      return;
    }
    
    // Check if user can edit settings
    if (!canEditSettings) {
      setError('You do not have permission to change settings on this account');
      return;
    }
    
    // Optimistically update UI
    setRogueMonitoring(newValue);
    setUpdatingRogueMonitoring(true);
    setError(null);
    
    try {
      await orgPropAPI.updateUserPreferences(
        token,
        userId,
        { rogue_monitoring_allowed: newValue }
      );
      
      setSuccessMessage(
        newValue 
          ? 'Rogue monitoring notifications enabled' 
          : 'Rogue monitoring notifications disabled'
      );
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      console.error('Error updating preference:', err);
      setError(`Failed to update preference: ${err.message}`);
      
      // Revert toggle on error
      setRogueMonitoring(!newValue);
    } finally {
      setUpdatingRogueMonitoring(false);
    }
  };

  // Fetch organization profile
  const fetchProfile = async () => {
    const token = auth.user?.access_token;
    if (!token || !orgHash) return;
    
    try {
      setLoadingProfile(true);
      const data = await orgPropAPI.getOrgProfile(token, orgHash);
      setProfileData(data);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile data');
    } finally {
      setLoadingProfile(false);
    }
  };
  
  // Fetch organization statistics
  const fetchStats = async () => {
    const token = auth.user?.access_token;
    if (!token || !orgHash) return;
    
    try {
      setLoadingStats(true);
      const data = await orgPropAPI.getOrgStats(token, orgHash);
      setStatsData(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to load statistics');
    } finally {
      setLoadingStats(false);
    }
  };
  
  // Fetch organization users
  const fetchUsers = async () => {
    const token = auth.user?.access_token;
    if (!token || !orgHash) return;
    
    try {
      setLoadingUsers(true);
      
      // Check if this is demo account
      if (isDemoAccount) {
        // Use fake demo users instead of API call
        setUsersData(fakeDemoUsers);
        setLoadingUsers(false);
        return;
      }
      
      // For real accounts, fetch from API
      const data = await orgPropAPI.getOrgUsers(token, orgHash);
      setUsersData(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };
  // const fetchUsers = async () => {
  //   const token = auth.user?.access_token;
  //   if (!token || !orgHash) return;
    
  //   try {
  //     setLoadingUsers(true);
  //     const data = await orgPropAPI.getOrgUsers(token, orgHash);
  //     setUsersData(data.users || []);
  //   } catch (err) {
  //     console.error('Error fetching users:', err);
  //     setError('Failed to load users');
  //   } finally {
  //     setLoadingUsers(false);
  //   }
  // };
  
  // Add new user
  const handleAddUser = async () => {
    const token = auth.user?.access_token;
    if (!token || !orgHash) return;
    
    if (!newUserEmail) {
      setError('Email is required');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUserEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    
    // Check user limit
    if (isUserLimitReached) {
      setError('User limit reached. Please upgrade your plan or contact support.');
      return;
    }
    
    try {
      setAddingUser(true);
      setError(null);
      
      await orgPropAPI.addOrgUser(token, {
        org: orgHash,
        email: newUserEmail,
        role: newUserRole
      });
      
      // Reset form and close modal
      setNewUserEmail('');
      setNewUserRole('user');
      setShowAddUserModal(false);
      
      // Refresh users list
      await fetchUsers();
      
      setSuccessMessage(`User ${newUserEmail} added successfully! They will receive an email with login details.`);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error adding user:', err);
      setError(`Failed to add user: ${err.message}`);
    } finally {
      setAddingUser(false);
    }
  };
  
  // Remove user
  const handleRemoveUser = async (userID, email) => {
    const token = auth.user?.access_token;
    if (!token || !orgHash) return;
    
    if (!window.confirm(`Are you sure you want to remove ${email}?\n\nThis will delete their account and revoke all access.`)) {
      return;
    }
    
    try {
      setError(null);
      
      await orgPropAPI.removeOrgUser(token, orgHash, userID);
      
      // Refresh users list
      await fetchUsers();
      
      setSuccessMessage(`User ${email} removed successfully`);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error removing user:', err);
      setError(`Failed to remove user: ${err.message}`);
    }
  };
  
  // Handle settings toggles
  const handleToggleSuccessToasts = () => {
    if (!canEditSettings) {
      setError('You do not have permission to change settings on this account');
      return;
    }
    
    if (hideSuccessToasts) {
      localStorage.removeItem('hideSuccessToasts');
      setHideSuccessToasts(false);
      setSuccessMessage('Success notifications enabled');
    } else {
      localStorage.setItem('hideSuccessToasts', 'true');
      setHideSuccessToasts(true);
      setSuccessMessage('Success notifications disabled');
    }
    
    setTimeout(() => setSuccessMessage(null), 3000);
  };
  
  // Load initial data
  useEffect(() => {
    if (orgHash && auth.user?.access_token) {
      // Fetch all data in parallel
      Promise.all([
        fetchProfile(),
        fetchStats(),
        fetchUsers(),
        fetchUserPreferences()
      ]).then(() => {
        setHasLoadedOnce(true);
      });
    }
    
    // Load settings from localStorage
    setHideSuccessToasts(localStorage.getItem('hideSuccessToasts') === 'true');
  }, [orgHash, auth.user?.access_token]);
  
  // Check if user can manage users
  const canManageUsers = (role === 'admin' || role === 'superadmin') && canEditSettings;
  
  // Define table columns
  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor('email', {
        header: 'Email',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('role', {
        header: 'Role',
        cell: info => (
        <Badge style={{backgroundColor: info.getValue() === 'admin' ? '#1abc9c' : '#00a2ff',color: '#fff' }}>
            {info.getValue()}
        </Badge>
        ),
      })
    ];
    
    if (canManageUsers) {
      baseColumns.push(
        columnHelper.display({
          id: 'actions',
          header: 'Actions',
          cell: props => (
            <Button
              variant=""
              style={{backgroundColor:'#e74c3c',color:'#fff'}}
              size="sm"
              onClick={() => handleRemoveUser(props.row.original.userID, props.row.original.email)}
            >
              <i className="fas fa-trash me-1"></i>&nbsp;
              Remove
            </Button>
          ),
        })
      );
    }
    
    return baseColumns;
  }, [canManageUsers, columnHelper]);
  
  // Create table instance
  const table = useReactTable({
    data: usersData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });
  
  // Format contract status badge
  const getContractStatusBadge = () => {
    if (!profileData) return null;
    
    if (profileData.contract_until === 'indefinite') {
      return <Badge bg="" style={{color: '#ffffff', backgroundColor:'#00a2ff'}}>Indefinite</Badge>;
    }
    
    if (profileData.is_active) {
      return <Badge bg="" style={{color: '#ffffff', backgroundColor:'#1abc9c'}}>Active</Badge>;
    }
    
    return <Badge bg="danger" style={{color: '#ffffff', backgroundColor:'#e74c3c'}}>Expired</Badge>;
  };
  
  // Format contract details
  const getContractDetails = () => {
    if (!profileData) return null;
    
    if (profileData.contract_until === 'indefinitely') {
      return <Badge bg="" style={{color: '#ffffff', backgroundColor:'#00a2ff'}}>Indefinite</Badge>
    }
    
    if (profileData.days_remaining !== null) {
      return (
        <>
          {profileData.contract_until}
          <span className="text-muted ms-2">
            ({profileData.days_remaining} days remaining)
          </span>
        </>
      );
    }
    
    return profileData.contract_until;
  };

  const getRogueMonitoringDetails = () => {
    if (!profileData) return null;
    
    if (profileData.rogue_monitoring === true) {
      return <Badge bg="" style={{color: '#ffffff', backgroundColor:'#1abc9c'}}>Active</Badge>;
    }
    
    if (profileData.rogue_monitoring === false) {
      return <Badge bg="" style={{color: 'white', backgroundColor:'var(--gray)'}}>Inactive</Badge>;
    }
  };

  // Show full-page spinner on initial load
  if (isAnyLoading && !hasLoadedOnce) {
    return (
      <Container fluid className="mt-4">
        <h2 className="mb-4">Profile & Settings</h2>
        <Row className="mt-5">
          <Col className="text-center">
            <LoadingSpinner message="Loading profile data..." />
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <>
      <style>{`
        /* Custom Toggle Switch Styles */
        .custom-toggle-switch {
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
          margin: 0;
        }

        .custom-toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .custom-toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.3s;
          border-radius: 24px;
        }

        .custom-toggle-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }

        input:checked + .custom-toggle-slider {
          background-color: #1abc9c;
        }

        input:checked + .custom-toggle-slider:before {
          transform: translateX(20px);
        }

        input:disabled + .custom-toggle-slider {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .custom-toggle-switch:hover .custom-toggle-slider {
          background-color: #b3b3b3;
        }

        input:checked + .custom-toggle-slider:hover {
          background-color: #16a085;
        }
      `}</style>
      
      <Container fluid className="mt-4">
      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Success Alert */}
      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      
      <h2 className="mb-4">Profile & Settings</h2>
      
      {/* Account Status & Statistics Row */}
      <Row className="mb-4">
        {/* Account Status */}
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Account Status</h5>
            </Card.Header>
            <Card.Body>
              {profileData ? (
                <>
                  <div className="mb-3">
                    <span><strong>Contract Status: </strong>{getContractStatusBadge()}</span>
                  </div>
                  <div className="mb-3">
                    <span><strong>Contract Until: </strong>{getContractDetails()}</span>
                  </div>
                  <div className="mb-3">
                    <strong>User Limit: </strong>
                    {profileData.users_limit === 'unlimited' ? (
                      <Badge style={{color: '#ffffff', backgroundColor:'#1abc9c'}} bg="primary">Unlimited</Badge>
                    ) : (
                      <span>
                        {usersData.length} / {profileData.users_limit} users
                        {isUserLimitReached && (
                          <Badge bg="" style={{color: '#ffffff', backgroundColor:'#e74c3c'}} className="ms-2">Limit Reached</Badge>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="mb-3">
                    <span><strong>Rogue Monitoring: </strong>{getRogueMonitoringDetails()}</span>
                  </div>
                  
                  {/* Warning if contract expiring soon */}
                  {profileData.is_active && 
                   profileData.days_remaining !== null && 
                   profileData.days_remaining < 30 && (
                    <Alert variant="warning" className="mt-3 mb-0">
                      <i className="fas fa-exclamation-triangle me-2"></i>&nbsp;
                      Your contract expires in {profileData.days_remaining} days. 
                      Please contact support to renew.
                    </Alert>
                  )}
                  
                  {/* Expired contract alert */}
                  {!profileData.is_active && (
                    <Alert variant="danger" className="mt-3 mb-0">
                      <i className="fas fa-times-circle me-2"></i>&nbsp;
                      Your contract has expired. Please contact support to renew access.
                    </Alert>
                  )}
                </>
              ) : (
                <Alert variant="info" className="mb-0">
                  No account information available
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        {/* Usage Statistics */}
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Usage Statistics</h5>
            </Card.Header>
            <Card.Body>
              {statsData ? (
                <>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <strong>Domains Under Management:</strong>
                      <Badge bg="primary" style={{fontSize: '1rem', color: '#fff', backgroundColor: '#1abc9c', paddingLeft: '1rem',paddingRight: '1rem', minWidth: '50px'}}>
                        {statsData.domains.toLocaleString()}
                      </Badge>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <strong>Properties Under Management:</strong>
                      <Badge bg="primary" style={{fontSize: '1rem', color: '#fff', backgroundColor: '#1abc9c', paddingLeft: '1rem',paddingRight: '1rem', minWidth: '50px'}}>
                        {statsData.properties.toLocaleString()}
                      </Badge>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <strong>Users Under Management:</strong>
                      <Badge bg="primary" style={{fontSize: '1rem', color: '#fff', backgroundColor: '#1abc9c', paddingLeft: '1rem',paddingRight: '1rem', minWidth: '50px'}}>
                        {statsData.users.toLocaleString()}
                      </Badge>
                    </div>
                  </div>
                  {statsData.date && (
                    <p className="mb-0 text-muted" style={{fontSize: '0.875rem', textAlign: 'start'}}>
                      <i className="fas fa-clock me-1"></i>&nbsp;
                      Last updated: {statsData.date}
                    </p>
                  )}
                </>
              ) : (
                <Alert variant="info" className="mb-0">
                  No statistics available yet. Data will appear after your first job run.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* User Management & Settings Row */}
      <Row className="mb-4">
        {/* User Management Section */}
        <Col lg={8}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-users me-2"></i>&nbsp;
                Organization Users
              </h5>
              {canManageUsers && (
                <Button 
                  variant="" 
                  size="sm"
                  style={{color: '#ffffff', backgroundColor:'#1abc9c'}}
                  onClick={() => setShowAddUserModal(true)}
                  disabled={isUserLimitReached}
                >
                  <i className="fas fa-plus me-1"></i>&nbsp; 
                  {isUserLimitReached ? 'User Limit Reached' : 'Add User'}
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {usersData.length > 0 ? (
                <>
                  {/* TanStack Table */}
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead style={{ backgroundColor: '#343a40', color: 'white' }}>
                        {table.getHeaderGroups().map(headerGroup => (
                          <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                              <th
                                key={header.id}
                                style={{ 
                                  textAlign: 'center',
                                  verticalAlign: 'middle',
                                  cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                  userSelect: 'none',
                                }}
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                  {header.column.getCanSort() && (
                                    <span style={{ fontSize: '0.8em' }}>
                                      {header.column.getIsSorted() === 'asc' ? '▲' : 
                                      header.column.getIsSorted() === 'desc' ? '▼' : 
                                      '⇅'}
                                    </span>
                                  )}
                                </div>
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody>
                        {table.getRowModel().rows.map(row => (
                          <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                              <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  
                  {/* Pagination Controls */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      <span className="me-2">
                        Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                        {Math.min(
                          (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                          usersData.length
                        )}{' '}
                        of {usersData.length} entries&nbsp;
                      </span>
                      <Form.Select
                        value={table.getState().pagination.pageSize}
                        onChange={e => table.setPageSize(Number(e.target.value))}
                        style={{ width: 'auto', display: 'inline-block' }}
                        size="sm"
                      >
                        {[10, 25, 50, 100].map(pageSize => (
                          <option key={pageSize} value={pageSize}>
                            Show {pageSize}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                    
                    <div className="d-flex justify-content-md-end">
                        <span className="me-3 align-self-center text-muted" style={{color:'var(--dark)'}}>
                        Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
                        </span>
                        <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                        className="me-1"
                        >
                        Previous
                        </Button>&nbsp;
                        <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                        >
                        Next
                        </Button>
                    </div>
                  </div>
                </>
              ) : (
                <Alert variant="info" className="mb-0 text-center" style={{color: "white", backgroundColor: "var(--gray)"}}>
                  <i className="fas fa-info-circle me-2"></i>&nbsp;
                  No users found in this organization
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        {/* Settings Section */}
        <Col lg={4}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-cog me-2"></i>&nbsp;
                Settings
              </h5>
            </Card.Header>
            <Card.Body>
              {/* Demo account warning */}
              {isDemoAccount && !isSuperadmin && (
                <Alert variant="warning" className="mb-3">
                  <i className="fas fa-info-circle me-2"></i>&nbsp;
                  This is a demo account. Settings cannot be changed.
                </Alert>
              )}
              
              <Form>
                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <Form.Label className="mb-0">Hide Add/Remove users notifications</Form.Label>
                    <label className="custom-toggle-switch" id="toast">
                      <input 
                        type="checkbox" 
                        checked={hideSuccessToasts}
                        onChange={handleToggleSuccessToasts}
                        disabled={!canEditSettings}
                      />
                      <span className="custom-toggle-slider"></span>
                    </label>
                  </div>
                  <Form.Text className="text-muted" style={{textAlign: 'start'}}>
                    Toggle to hide/show success notification on adding add/remove jobs to backlog.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <Form.Label className="mb-0">
                      Rogue Monitoring Notifications
                      {updatingRogueMonitoring && (
                        <Spinner 
                          animation="border" 
                          size="sm" 
                          className="ms-2" 
                          style={{ width: '1rem', height: '1rem' }}
                        />
                      )}
                    </Form.Label>
                    <label className="custom-toggle-switch" id="RogueMonitoring">
                      <input 
                        type="checkbox" 
                        checked={rogueMonitoring}
                        onChange={handleToggleRogueMonitoring}
                        disabled={updatingRogueMonitoring || !canEditSettings}
                      />
                      <span className="custom-toggle-slider"></span>
                    </label>
                  </div>
                  <Form.Text className="text-muted" style={{textAlign: 'start'}}>
                    Toggle sending emails on new hosts and verified users found
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <Form.Label className="mb-0">
                    Restart Tutorial
                      {updatingRogueMonitoring && (
                        <Spinner 
                          animation="border" 
                          size="sm" 
                          className="ms-2" 
                          style={{ width: '1rem', height: '1rem' }}
                        />
                      )}
                    </Form.Label>
                    <label className="custom-toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={false}
                        onChange={handleRestartTour}
                        // disabled={updatingRogueMonitoring || !canEditSettings}
                      />
                      <span className="custom-toggle-slider"></span>
                    </label>
                  </div>
                  <Form.Text className="text-muted" style={{textAlign: 'start'}}>
                  Start the guided tour from the beginning
                  </Form.Text>
                </Form.Group>

                {/* NEW: Restart Tour Toggle
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    Restart Tutorial
                    <div className="text-muted small">
                      Start the guided tour from the beginning
                    </div>
                  </div>
                  <label className="custom-toggle-switch">
                    <input
                      type="checkbox"
                      onChange={handleRestartTour}
                      checked={false}
                    />
                    <span className="custom-toggle-slider"></span>
                  </label>
                </div> */}
                
                <hr />
                
                <div className="text-muted mb-3">
                  <h6>Coming Soon</h6>
                  <p className="mb-2" style={{fontSize: '0.875rem'}}>
                    <i className="fas fa-bell me-2"></i>&nbsp;
                    Features available in future updates:
                  </p>
                </div>
                
                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <Form.Label className="mb-0">Rogue Monitoring SMS</Form.Label>
                    <label className="custom-toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={newOwnerAlert}
                        onChange={(e) => setNewOwnerAlert(e.target.checked)}
                        disabled
                      />
                      <span className="custom-toggle-slider"></span>
                    </label>
                  </div>
                  <Form.Text className="text-muted" style={{textAlign: 'start'}}>
                    Receive SMS when new Owner Verified of new subdomain gets discovered
                  </Form.Text>
                </Form.Group>
                
                {/* <Form.Group className="mb-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <Form.Label className="mb-0">New Host Alert</Form.Label>
                    <label className="custom-toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={newHostAlert}
                        onChange={(e) => setNewHostAlert(e.target.checked)}
                        disabled
                      />
                      <span className="custom-toggle-slider"></span>
                    </label>
                  </div>
                  <Form.Text className="text-muted" style={{textAlign: 'start'}}>
                    Email alerts for unknown hosts
                  </Form.Text>
                </Form.Group> */}
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Add User Modal */}
      <Modal show={showAddUserModal} onHide={() => setShowAddUserModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-user-plus me-2"></i>&nbsp;
            Add New User
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* User limit warning */}
          {profileData && 
           profileData.users_limit !== 'unlimited' && 
           usersData.length >= parseInt(profileData.users_limit) - 2 && (
            <Alert variant="warning" className="mb-3" style={{color: '#ffffff', backgroundColor:'#e74c3c'}} >
              <i className="fas fa-exclamation-triangle me-2"></i>
              You're approaching your user limit ({usersData.length}/{profileData.users_limit})
            </Alert>
          )}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                placeholder="user@example.com"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                required
                autoFocus
              />
              <Form.Text className="text-muted">
                User will receive an email with temporary password
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Role <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value)}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Form.Select>
              <Form.Text className="text-muted">
                <strong>User:</strong> Can view data and manage permissions
                <br />
                <strong>Admin:</strong> Can manage users and has full access
              </Form.Text>
            </Form.Group>
          </Form>
          
          <Alert variant="" className="mb-0" style={{backgroundColor: '#b5e2fb',borderColor: '#00a2ff', color: 'black'}}>
            <i className="fas fa-info-circle me-2"></i>&nbsp;
            no-reply@verificationemail.com will send the new user an email with their temporary password. 
            User must change it on first login.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddUserModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddUser}
            disabled={addingUser || !newUserEmail || isUserLimitReached}
          >
            {addingUser ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Adding User...
              </>
            ) : (
              <>
                <i className="fas fa-check me-1"></i>
                Add User
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </>
  );
};

export default ProfilePage;




























// import React, { useState, useEffect, useMemo } from 'react';
// import { 
//   Container, 
//   Row, 
//   Col, 
//   Card, 
//   Button, 
//   Table,
//   Form, 
//   Modal, 
//   Spinner, 
//   Alert,
//   Badge 
// } from 'react-bootstrap';
// import {
//   useReactTable,
//   getCoreRowModel,
//   flexRender,
//   getPaginationRowModel,
//   getSortedRowModel,
//   createColumnHelper,
// } from '@tanstack/react-table';
// import { useAuth } from "react-oidc-context";
// import { useUser } from "../context/UserContext";
// import { orgPropAPI } from "../utils/apiClient";

// const columnHelper = createColumnHelper();

// function ProfilePage() {
//   const auth = useAuth();
//   useEffect(() => {
//     document.title = 'Profile Page - LostLogic';
//   }, []);
//   const { orgHash, role } = useUser();
  
//   // State for profile data
//   const [profileData, setProfileData] = useState(null);
//   const [statsData, setStatsData] = useState(null);
//   const [usersData, setUsersData] = useState([]);
  
//   // Loading states
//   const [loadingProfile, setLoadingProfile] = useState(true);
//   const [loadingStats, setLoadingStats] = useState(true);
//   const [loadingUsers, setLoadingUsers] = useState(true);
  
//   // Settings toggles
//   const [hideSuccessToasts, setHideSuccessToasts] = useState(false);
//   const [rogueMonitoring, setRogueMonitoring] = useState(true); // NEW STATE
//   const [updatingRogueMonitoring, setUpdatingRogueMonitoring] = useState(false); // NEW STATE
//   const [newOwnerAlert, setNewOwnerAlert] = useState(false);
//   const [newHostAlert, setNewHostAlert] = useState(false);
  
//   // Add user modal
//   const [showAddUserModal, setShowAddUserModal] = useState(false);
//   const [newUserEmail, setNewUserEmail] = useState('');
//   const [newUserRole, setNewUserRole] = useState('user');
//   const [addingUser, setAddingUser] = useState(false);
  
//   // Table pagination
//   const [pagination, setPagination] = useState({
//     pageIndex: 0,
//     pageSize: 10,
//   });
  
//   // Errors
//   const [error, setError] = useState(null);
//   const [successMessage, setSuccessMessage] = useState(null);
  

//   // NEW: Fetch user's current rogue monitoring preference
//   const fetchUserPreferences = async () => {
//     const token = auth.user?.access_token;
//     const userId = auth.user?.profile?.sub;
    
//     if (!token || !userId) return;
    
//     try {
//       // Get user data from org users list to check current preference
//       const data = await orgPropAPI.getOrgUsers(token, orgHash);
//       const currentUser = data.users?.find(u => u.userID === userId);
      
//       if (currentUser) {
//         // Set rogue monitoring state based on user's preference
//         // Default to true if not set
//         setRogueMonitoring(currentUser.rogue_monitoring_allowed !== false);
//       }
//     } catch (err) {
//       console.error('Error fetching user preferences:', err);
//       // Default to enabled if error
//       setRogueMonitoring(true);
//     }
//   };
  
//   // NEW: Handle rogue monitoring toggle
//   const handleToggleRogueMonitoring = async (e) => {
//     const newValue = e.target.checked;
//     const token = auth.user?.access_token;
//     const userId = auth.user?.profile?.sub;
    
//     if (!token || !userId) {
//       setError('Authentication required');
//       return;
//     }
    
//     // Optimistically update UI
//     setRogueMonitoring(newValue);
//     setUpdatingRogueMonitoring(true);
//     setError(null);
    
//     try {
//       await orgPropAPI.updateUserPreferences(
//         token,
//         userId,
//         { rogue_monitoring_allowed: newValue }
//       );
      
//       setSuccessMessage(
//         newValue 
//           ? 'Rogue monitoring notifications enabled' 
//           : 'Rogue monitoring notifications disabled'
//       );
      
//       // Clear success message after 3 seconds
//       setTimeout(() => setSuccessMessage(null), 3000);
      
//     } catch (err) {
//       console.error('Error updating preference:', err);
//       setError(`Failed to update preference: ${err.message}`);
      
//       // Revert toggle on error
//       setRogueMonitoring(!newValue);
//     } finally {
//       setUpdatingRogueMonitoring(false);
//     }
//   };


//   // Fetch organization profile
//   const fetchProfile = async () => {
//     const token = auth.user?.access_token;
//     if (!token || !orgHash) return;
    
//     try {
//       setLoadingProfile(true);
//       const data = await orgPropAPI.getOrgProfile(token, orgHash);
//       setProfileData(data);
//     } catch (err) {
//       console.error('Error fetching profile:', err);
//       setError('Failed to load profile data');
//     } finally {
//       setLoadingProfile(false);
//     }
//   };
  
//   // Fetch organization statistics
//   const fetchStats = async () => {
//     const token = auth.user?.access_token;
//     if (!token || !orgHash) return;
    
//     try {
//       setLoadingStats(true);
//       const data = await orgPropAPI.getOrgStats(token, orgHash);
//       setStatsData(data);
//     } catch (err) {
//       console.error('Error fetching stats:', err);
//       setError('Failed to load statistics');
//     } finally {
//       setLoadingStats(false);
//     }
//   };
  
//   // Fetch organization users
//   const fetchUsers = async () => {
//     const token = auth.user?.access_token;
//     if (!token || !orgHash) return;
    
//     try {
//       setLoadingUsers(true);
//       const data = await orgPropAPI.getOrgUsers(token, orgHash);
//       setUsersData(data.users || []);
//     } catch (err) {
//       console.error('Error fetching users:', err);
//       setError('Failed to load users');
//     } finally {
//       setLoadingUsers(false);
//     }
//   };
  
//   // Add new user
//   const handleAddUser = async () => {
//     const token = auth.user?.access_token;
//     if (!token || !orgHash) return;
    
//     if (!newUserEmail) {
//       setError('Email is required');
//       return;
//     }
    
//     // Basic email validation
//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     if (!emailRegex.test(newUserEmail)) {
//       setError('Please enter a valid email address');
//       return;
//     }
    
//     try {
//       setAddingUser(true);
//       setError(null);
      
//       await orgPropAPI.addOrgUser(token, {
//         org: orgHash,
//         email: newUserEmail,
//         role: newUserRole
//       });
      
//       // Reset form and close modal
//       setNewUserEmail('');
//       setNewUserRole('user');
//       setShowAddUserModal(false);
      
//       // Refresh users list
//       await fetchUsers();
      
//       setSuccessMessage(`User ${newUserEmail} added successfully! They will receive an email with login details.`);
      
//       // Clear success message after 5 seconds
//       setTimeout(() => setSuccessMessage(null), 5000);
//     } catch (err) {
//       console.error('Error adding user:', err);
//       setError(`Failed to add user: ${err.message}`);
//     } finally {
//       setAddingUser(false);
//     }
//   };
  
//   // Remove user
//   const handleRemoveUser = async (userID, email) => {
//     const token = auth.user?.access_token;
//     if (!token || !orgHash) return;
    
//     if (!window.confirm(`Are you sure you want to remove ${email}?\n\nThis will delete their account and revoke all access.`)) {
//       return;
//     }
    
//     try {
//       setError(null);
      
//       await orgPropAPI.removeOrgUser(token, orgHash, userID);
      
//       // Refresh users list
//       await fetchUsers();
      
//       setSuccessMessage(`User ${email} removed successfully`);
      
//       // Clear success message after 5 seconds
//       setTimeout(() => setSuccessMessage(null), 5000);
//     } catch (err) {
//       console.error('Error removing user:', err);
//       setError(`Failed to remove user: ${err.message}`);
//     }
//   };
  
//   // // Handle settings toggles
//   // const handleToggleSuccessToasts = () => {
//   //   if (hideSuccessToasts) {
//   //     localStorage.removeItem('hideSuccessToasts');
//   //     setHideSuccessToasts(false);
//   //     setSuccessMessage('Success notifications enabled');
//   //   } else {
//   //     localStorage.setItem('hideSuccessToasts', 'true');
//   //     setHideSuccessToasts(true);
//   //     setSuccessMessage('Success notifications disabled');
//   //   }
    
//   //   setTimeout(() => setSuccessMessage(null), 3000);
//   // };
  
//   // // Load initial data
//   // useEffect(() => {
//   //   if (orgHash && auth.user?.access_token) {
//   //     fetchProfile();
//   //     fetchStats();
//   //     fetchUsers();
//   //   }
//   // Handle settings toggles
//   const handleToggleSuccessToasts = () => {
//     if (hideSuccessToasts) {
//       localStorage.removeItem('hideSuccessToasts');
//       setHideSuccessToasts(false);
//       setSuccessMessage('Success notifications enabled');
//     } else {
//       localStorage.setItem('hideSuccessToasts', 'true');
//       setHideSuccessToasts(true);
//       setSuccessMessage('Success notifications disabled');
//     }
    
//     setTimeout(() => setSuccessMessage(null), 3000);
//   };
  
//   // Load initial data
//   useEffect(() => {
//     if (orgHash && auth.user?.access_token) {
//       fetchProfile();
//       fetchStats();
//       fetchUsers();
//       fetchUserPreferences(); // Fetch user preferences
//     }
    
//     // Load settings from localStorage
//     setHideSuccessToasts(localStorage.getItem('hideSuccessToasts') === 'true');
//   }, [orgHash, auth.user?.access_token]);
  
//   // Check if user can manage users
//   const canManageUsers = role === 'admin' || role === 'superadmin';
  
//   // Define table columns
//   const columns = useMemo(() => {
//     const baseColumns = [
//       columnHelper.accessor('email', {
//         header: 'Email',
//         cell: info => info.getValue(),
//       }),
//       columnHelper.accessor('role', {
//         header: 'Role',
//         cell: info => (
//         <Badge style={{backgroundColor: info.getValue() === 'admin' ? '#1abc9c' : '#00a2ff',color: '#fff' }}>
//             {info.getValue()}
//         </Badge>
//         ),
//       })
//     ];
    
//     if (canManageUsers) {
//       baseColumns.push(
//         columnHelper.display({
//           id: 'actions',
//           header: 'Actions',
//           cell: props => (
//             <Button
//               variant=""
//               Style="background-color:#e74c3c;color:#fff;"
//               size="sm"
//               onClick={() => handleRemoveUser(props.row.original.userID, props.row.original.email)}
//             >
//               <i className="fas fa-trash me-1"></i>&nbsp;
//               Remove
//             </Button>
//           ),
//         })
//       );
//     }
    
//     return baseColumns;
//   }, [canManageUsers, columnHelper]);
  
//   // Create table instance
//   const table = useReactTable({
//     data: usersData,
//     columns,
//     getCoreRowModel: getCoreRowModel(),
//     getPaginationRowModel: getPaginationRowModel(),
//     getSortedRowModel: getSortedRowModel(),
//     onPaginationChange: setPagination,
//     state: {
//       pagination,
//     },
//   });
  
//   // Format contract status badge
//   const getContractStatusBadge = () => {
//     if (!profileData) return null;
    
//     if (profileData.contract_until === 'indefinite') {
//       return <Badge bg="info" Style="color: #ffffff; background-color:#00a2ff!important;">Indefinite</Badge>;
//     }
    
//     if (profileData.is_active) {
//       return <Badge bg="success" Style="color: #ffffff; background-color:#1abc9c!important;">Active</Badge>;
//     }
    
//     return <Badge bg="danger" Style="color: #ffffff; background-color:#e74c3c!important;">Expired</Badge>;
//   };
  
//   // Format contract details
//   const getContractDetails = () => {
//     if (!profileData) return null;
    
//     if (profileData.contract_until === 'indefinite') {
//       return 'Indefinite';
//     }
    
//     if (profileData.days_remaining !== null) {
//       return (
//         <>
//           {profileData.contract_until}
//           <span className="text-muted ms-2">
//             ({profileData.days_remaining} days remaining)
//           </span>
//         </>
//       );
//     }
    
//     return profileData.contract_until;
//   };
  
//   return (
//     <>
//       <style>{`
//         /* Custom Toggle Switch Styles */
//         .custom-toggle-switch {
//           position: relative;
//           display: inline-block;
//           width: 44px;
//           height: 24px;
//           margin: 0;
//         }

//         .custom-toggle-switch input {
//           opacity: 0;
//           width: 0;
//           height: 0;
//         }

//         .custom-toggle-slider {
//           position: absolute;
//           cursor: pointer;
//           top: 0;
//           left: 0;
//           right: 0;
//           bottom: 0;
//           background-color: #ccc;
//           transition: 0.3s;
//           border-radius: 24px;
//         }

//         .custom-toggle-slider:before {
//           position: absolute;
//           content: "";
//           height: 18px;
//           width: 18px;
//           left: 3px;
//           bottom: 3px;
//           background-color: white;
//           transition: 0.3s;
//           border-radius: 50%;
//         }

//         input:checked + .custom-toggle-slider {
//           background-color: #1abc9c;
//         }

//         input:checked + .custom-toggle-slider:before {
//           transform: translateX(20px);
//         }

//         input:disabled + .custom-toggle-slider {
//           opacity: 0.5;
//           cursor: not-allowed;
//         }

//         .custom-toggle-switch:hover .custom-toggle-slider {
//           background-color: #b3b3b3;
//         }

//         input:checked + .custom-toggle-slider:hover {
//           background-color: #16a085;
//         }
//       `}</style>
      
//       <Container fluid className="mt-4">
//       {/* Error Alert */}
//       {error && (
//         <Alert variant="danger" dismissible onClose={() => setError(null)}>
//           {error}
//         </Alert>
//       )}
      
//       {/* Success Alert */}
//       {successMessage && (
//         <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
//           {successMessage}
//         </Alert>
//       )}
      
//       <h2 className="mb-4">Profile & Settings</h2>
      
//       {/* Account Status & Statistics Row */}
//       <Row className="mb-4">
//         {/* Account Status */}
//         <Col md={6}>
//           <Card>
//             <Card.Header>
//               <h5 className="mb-0">Account Status</h5>
//             </Card.Header>
//             <Card.Body>
//               {loadingProfile ? (
//                 <div className="text-center p-3">
//                   <Spinner animation="border" size="sm" />
//                   <p className="text-muted mt-2 mb-0">Loading account status...</p>
//                 </div>
//               ) : profileData ? (
//                 <>
//                   <div className="mb-3">
//                     <strong>Contract Status: </strong>
//                     {getContractStatusBadge()}
//                   </div>
//                   <div className="mb-3">
//                     <strong>Contract Until: </strong>
//                     <div>{getContractDetails()}</div>
//                   </div>
//                   <div className="mb-0">
//                     <strong>User Limit: </strong>
//                     {profileData.users_limit === 'unlimited' ? (
//                       <Badge bg="success">Unlimited</Badge>
//                     ) : (
//                       <span>{profileData.users_limit} users</span>
//                     )}
//                   </div>
                  
//                   {/* Warning if contract expiring soon */}
//                   {profileData.is_active && 
//                    profileData.days_remaining !== null && 
//                    profileData.days_remaining < 30 && (
//                     <Alert variant="warning" className="mt-3 mb-0">
//                       <i className="fas fa-exclamation-triangle me-2"></i>&nbsp;
//                       Your contract expires in {profileData.days_remaining} days. 
//                       Please contact support to renew.
//                     </Alert>
//                   )}
                  
//                   {/* Expired contract alert */}
//                   {!profileData.is_active && (
//                     <Alert variant="danger" className="mt-3 mb-0">
//                       <i className="fas fa-times-circle me-2"></i>&nbsp;
//                       Your contract has expired. Please contact support to renew access.
//                     </Alert>
//                   )}
//                 </>
//               ) : (
//                 <Alert variant="info" className="mb-0">
//                   No account information available
//                 </Alert>
//               )}
//             </Card.Body>
//           </Card>
//         </Col>
        
//         {/* Usage Statistics */}
//         <Col md={6}>
//           <Card>
//             <Card.Header>
//               <h5 className="mb-0">Usage Statistics</h5>
//             </Card.Header>
//             <Card.Body>
//               {loadingStats ? (
//                 <div className="text-center p-3">
//                   <Spinner animation="border" size="sm" />
//                   <p className="text-muted mt-2 mb-0">Loading statistics...</p>
//                 </div>
//               ) : statsData ? (
//                 <>
//                   <div className="mb-3">
//                     <div className="d-flex justify-content-between align-items-center">
//                       <strong>Domains Under Management:</strong>
//                       <Badge bg="primary" style={{fontSize: '1rem', color: '#fff', backgroundColor: '#1abc9c', paddingLeft: '1rem',paddingRight: '1rem', minWidth: '50px'}}>
//                         {statsData.domains.toLocaleString()}
//                       </Badge>
//                     </div>
//                   </div>
//                   <div className="mb-3">
//                     <div className="d-flex justify-content-between align-items-center">
//                       <strong>Properties Under Management:</strong>
//                       <Badge bg="primary" style={{fontSize: '1rem', color: '#fff', backgroundColor: '#1abc9c', paddingLeft: '1rem',paddingRight: '1rem', minWidth: '50px'}}>
//                         {statsData.properties.toLocaleString()}
//                       </Badge>
//                     </div>
//                   </div>
//                   <div className="mb-3">
//                     <div className="d-flex justify-content-between align-items-center">
//                       <strong>Users Under Management:</strong>
//                       <Badge bg="primary" style={{fontSize: '1rem', color: '#fff', backgroundColor: '#1abc9c', paddingLeft: '1rem',paddingRight: '1rem', minWidth: '50px'}}>
//                         {statsData.users.toLocaleString()}
//                       </Badge>
//                     </div>
//                   </div>
//                   {statsData.date && (
//                     <p className="mb-0 text-muted" style={{fontSize: '0.875rem', textAlign: 'start'}}>
//                       <i className="fas fa-clock me-1"></i>&nbsp;
//                       Last updated: {statsData.date}
//                     </p>
//                   )}
//                 </>
//               ) : (
//                 <Alert variant="info" className="mb-0">
//                   No statistics available yet. Data will appear after your first job run.
//                 </Alert>
//               )}
//             </Card.Body>
//           </Card>
//         </Col>
//       </Row>
      
//       {/* User Management & Settings Row */}
//       <Row className="mb-4">
//         {/* User Management Section */}
//         <Col lg={8}>
//           <Card>
//             <Card.Header className="d-flex justify-content-between align-items-center">
//               <h5 className="mb-0">
//                 <i className="fas fa-users me-2"></i>&nbsp;
//                 Organization Users
//               </h5>
//               {canManageUsers && (
//                 <Button 
//                   variant="" 
//                   size="sm"
//                   Style="color: #ffffff; background-color:#1abc9c!important;"
//                   onClick={() => setShowAddUserModal(true)}
//                 >
//                   <i className="fas fa-plus me-1"></i>&nbsp; Add User
//                 </Button>
//               )}
//             </Card.Header>
//             <Card.Body>
//               {loadingUsers ? (
//                 <div className="text-center p-4">
//                   <Spinner animation="border" />
//                   <p className="text-muted mt-3 mb-0">Loading users...</p>
//                 </div>
//               ) : usersData.length > 0 ? (
//                 <>
//                   {/* TanStack Table */}
//                   <div className="table-responsive">
//                     <Table striped bordered hover>
//                       <thead style={{ backgroundColor: '#343a40', color: 'white' }}>
//                         {table.getHeaderGroups().map(headerGroup => (
//                           <tr key={headerGroup.id}>
//                             {headerGroup.headers.map(header => (
//                               <th
//                                 key={header.id}
//                                 style={{ 
//                                   textAlign: 'center',
//                                   verticalAlign: 'middle',
//                                   cursor: header.column.getCanSort() ? 'pointer' : 'default',
//                                   userSelect: 'none',
//                                 }}
//                                 onClick={header.column.getToggleSortingHandler()}
//                               >
//                                 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
//                                   {flexRender(
//                                     header.column.columnDef.header,
//                                     header.getContext()
//                                   )}
//                                   {header.column.getCanSort() && (
//                                     <span style={{ fontSize: '0.8em' }}>
//                                       {header.column.getIsSorted() === 'asc' ? '▲' : 
//                                       header.column.getIsSorted() === 'desc' ? '▼' : 
//                                       '⇅'}
//                                     </span>
//                                   )}
//                                 </div>
//                               </th>
//                             ))}
//                           </tr>
//                         ))}
//                       </thead>
//                       <tbody>
//                         {table.getRowModel().rows.map(row => (
//                           <tr key={row.id}>
//                             {row.getVisibleCells().map(cell => (
//                               <td key={cell.id}>
//                                 {flexRender(cell.column.columnDef.cell, cell.getContext())}
//                               </td>
//                             ))}
//                           </tr>
//                         ))}
//                       </tbody>
//                     </Table>
//                   </div>
                  
//                   {/* Pagination Controls */}
//                   <div className="d-flex justify-content-between align-items-center mt-3">
//                     <div>
//                       <span className="me-2">
//                         Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
//                         {Math.min(
//                           (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
//                           usersData.length
//                         )}{' '}
//                         of {usersData.length} entries&nbsp;
//                       </span>
//                       <Form.Select
//                         value={table.getState().pagination.pageSize}
//                         onChange={e => table.setPageSize(Number(e.target.value))}
//                         style={{ width: 'auto', display: 'inline-block' }}
//                         size="sm"
//                       >
//                         {[10, 25, 50, 100].map(pageSize => (
//                           <option key={pageSize} value={pageSize}>
//                             Show {pageSize}
//                           </option>
//                         ))}
//                       </Form.Select>
//                     </div>
                    

        
//                     <div className="d-flex justify-content-md-end">
//                         <span className="me-3 align-self-center text-muted" Style="color:var(--dark);">
//                         Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
//                         </span>
//                         <Button
//                         variant="outline-primary"
//                         size="sm"
//                         onClick={() => table.previousPage()}
//                         disabled={!table.getCanPreviousPage()}
//                         className="me-1"
//                         >
//                         Previous
//                         </Button>&nbsp;
//                         <Button
//                         variant="outline-primary"
//                         size="sm"
//                         onClick={() => table.nextPage()}
//                         disabled={!table.getCanNextPage()}
//                         >
//                         Next
//                         </Button>
//                     </div>
        
//                     {/* <div className="btn-group">
//                       <Button
//                         variant="outline-secondary"
//                         size="sm"
//                         onClick={() => table.setPageIndex(0)}
//                         disabled={!table.getCanPreviousPage()}
//                       >
//                         {'<<'}
//                       </Button>
//                       <Button
//                         variant="outline-secondary"
//                         size="sm"
//                         onClick={() => table.previousPage()}
//                         disabled={!table.getCanPreviousPage()}
//                       >
//                         {'<'}
//                       </Button>
//                       <Button
//                         variant="outline-secondary"
//                         size="sm"
//                         onClick={() => table.nextPage()}
//                         disabled={!table.getCanNextPage()}
//                       >
//                         {'>'}
//                       </Button>
//                       <Button
//                         variant="outline-secondary"
//                         size="sm"
//                         onClick={() => table.setPageIndex(table.getPageCount() - 1)}
//                         disabled={!table.getCanNextPage()}
//                       >
//                         {'>>'}
//                       </Button>
//                     </div> */}
//                   </div>
//                 </>
//               ) : (
//                 <Alert variant="info" className="mb-0 text-center">
//                   <i className="fas fa-info-circle me-2"></i>&nbsp;
//                   No users found in this organization
//                 </Alert>
//               )}
//             </Card.Body>
//           </Card>
//         </Col>
        
//         {/* Settings Section */}
//         <Col lg={4}>
//           <Card>
//             <Card.Header>
//               <h5 className="mb-0">
//                 <i className="fas fa-cog me-2"></i>&nbsp;
//                 Settings
//               </h5>
//             </Card.Header>
//             <Card.Body>
//               <Form>
//                 <Form.Group className="mb-3">
//                   <div className="d-flex justify-content-between align-items-center">
//                     <Form.Label className="mb-0">Hide Success Notifications</Form.Label>
//                     <label className="custom-toggle-switch">
//                       <input 
//                         type="checkbox" 
//                         checked={hideSuccessToasts}
//                         onChange={handleToggleSuccessToasts}
//                       />
//                       <span className="custom-toggle-slider"></span>
//                     </label>
//                   </div>
//                   <Form.Text className="text-muted" Style="text-align: start;">
//                     Toggle to hide/show success notification toasts
//                   </Form.Text>
//                 </Form.Group>

//                 {/* <Form.Group className="mb-3">
//                   <div className="d-flex justify-content-between align-items-center">
//                     <Form.Label className="mb-0">Rogue Monitoring Notifications</Form.Label>
//                     <label className="custom-toggle-switch">
//                       <input 
//                         type="checkbox" 
//                       />
//                       <span className="custom-toggle-slider"></span>
//                     </label>
//                   </div>
//                   <Form.Text className="text-muted" Style="text-align: start;">
//                     Toggle sending emails on new hosts and verified users found
//                   </Form.Text>
//                 </Form.Group> */}
//                 <Form.Group className="mb-3">
//                 <div className="d-flex justify-content-between align-items-center">
//                   <Form.Label className="mb-0">
//                     Rogue Monitoring Notifications
//                     {updatingRogueMonitoring && (
//                       <Spinner 
//                         animation="border" 
//                         size="sm" 
//                         className="ms-2" 
//                         style={{ width: '1rem', height: '1rem' }}
//                       />
//                     )}
//                   </Form.Label>
//                   <label className="custom-toggle-switch">
//                     <input 
//                       type="checkbox" 
//                       checked={rogueMonitoring}
//                       onChange={handleToggleRogueMonitoring}
//                       disabled={updatingRogueMonitoring}
//                     />
//                     <span className="custom-toggle-slider"></span>
//                   </label>
//                 </div>
//                 <Form.Text className="text-muted" Style="text-align: start;">
//                   Toggle sending emails on new hosts and verified users found
//                 </Form.Text>
//               </Form.Group>
                
//                 <hr />
                
//                 <div className="text-muted mb-3">
//                   <h6>Coming Soon</h6>
//                   <p className="mb-2" style={{fontSize: '0.875rem'}}>
//                     <i className="fas fa-bell me-2"></i>&nbsp;
//                     Alert features available in future updates:
//                   </p>
//                 </div>
                
//                 <Form.Group className="mb-3">
//                   <div className="d-flex justify-content-between align-items-center">
//                     <Form.Label className="mb-0">New Owner Alert</Form.Label>
//                     <label className="custom-toggle-switch">
//                       <input 
//                         type="checkbox" 
//                         checked={newOwnerAlert}
//                         onChange={(e) => setNewOwnerAlert(e.target.checked)}
//                         disabled
//                       />
//                       <span className="custom-toggle-slider"></span>
//                     </label>
//                   </div>
//                   <Form.Text className="text-muted" Style="text-align: start;">
//                     Email alerts for new verified owners
//                   </Form.Text>
//                 </Form.Group>
                
//                 <Form.Group className="mb-0">
//                   <div className="d-flex justify-content-between align-items-center">
//                     <Form.Label className="mb-0">New Host Alert</Form.Label>
//                     <label className="custom-toggle-switch">
//                       <input 
//                         type="checkbox" 
//                         checked={newHostAlert}
//                         onChange={(e) => setNewHostAlert(e.target.checked)}
//                         disabled
//                       />
//                       <span className="custom-toggle-slider"></span>
//                     </label>
//                   </div>
//                   <Form.Text className="text-muted" Style="text-align: start;">
//                     Email alerts for unknown hosts
//                   </Form.Text>
//                 </Form.Group>
//               </Form>
//             </Card.Body>
//           </Card>
//         </Col>
//       </Row>
      
//       {/* Add User Modal */}
//       <Modal show={showAddUserModal} onHide={() => setShowAddUserModal(false)} centered>
//         <Modal.Header closeButton>
//           <Modal.Title>
//             <i className="fas fa-user-plus me-2"></i>&nbsp;
//             Add New User
//           </Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <Form>
//             <Form.Group className="mb-3">
//               <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
//               <Form.Control
//                 type="email"
//                 placeholder="user@example.com"
//                 value={newUserEmail}
//                 onChange={(e) => setNewUserEmail(e.target.value)}
//                 required
//                 autoFocus
//               />
//               <Form.Text className="text-muted">
//                 User will receive an email with temporary password
//               </Form.Text>
//             </Form.Group>
            
//             <Form.Group className="mb-3">
//               <Form.Label>Role <span className="text-danger">*</span></Form.Label>
//               <Form.Select
//                 value={newUserRole}
//                 onChange={(e) => setNewUserRole(e.target.value)}
//               >
//                 <option value="user">User</option>
//                 <option value="admin">Admin</option>
//               </Form.Select>
//               <Form.Text className="text-muted">
//                 <strong>User:</strong> Can view data and manage permissions
//                 <br />
//                 <strong>Admin:</strong> Can manage users and has full access
//               </Form.Text>
//             </Form.Group>
//           </Form>
          
//           <Alert variant="info" className="mb-0">
//             <i className="fas fa-info-circle me-2"></i>&nbsp;
//             The new user will receive an email from AWS Cognito with their temporary password. 
//             They must change it on first login.
//           </Alert>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="secondary" onClick={() => setShowAddUserModal(false)}>
//             Cancel
//           </Button>
//           <Button 
//             variant="primary" 
//             onClick={handleAddUser}
//             disabled={addingUser || !newUserEmail}
//           >
//             {addingUser ? (
//               <>
//                 <Spinner animation="border" size="sm" className="me-2" />
//                 Adding User...
//               </>
//             ) : (
//               <>
//                 <i className="fas fa-check me-1"></i>
//                 Add User
//               </>
//             )}
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </Container>
//     </>
//   );
// };

// export default ProfilePage;